import React from 'react';

import errorCodeList from '@client/config/errorCodeList';
import { SpectrogramPoint } from '@ledium/types';
import { Alert, Box, Grid, Typography } from '@mui/material';

import WavelengthChart from './WavelengthChart';

interface AutocompleteOption {
    name: string;
    description?: string;
    errorCode?: string;
    optionChart?: SpectrogramPoint[];
    additionalItemListInfo?: React.ReactNode;
    nameLineExtra?: React.ReactNode;
    lockedReason?: string;
    linkedReason?: string;
}

interface AutocompleteOptionWithChartProps {
    option: AutocompleteOption;
    errorCodes?: string;
    spectrogramData?: SpectrogramPoint[] | null;
    size?: 'large' | 'small';
    onDoubleClick?: React.MouseEventHandler;
}

function getErrorText(errorCodes: string | undefined, errorCode: string | undefined, errorCodeMap: typeof errorCodeList): string {
    if (!errorCodes || !errorCode) {
        return '';
    }
    return (errorCodeMap[errorCodes]?.[errorCode] as string | undefined) ?? '';
}

const AutocompleteOptionWithChart = ({ option, errorCodes, spectrogramData = null, size = 'large', onDoubleClick }: AutocompleteOptionWithChartProps) => {
    const spectrogram = spectrogramData || option.optionChart || null;
    const error = getErrorText(errorCodes, option.errorCode, errorCodeList);

    return (
        <Grid container spacing={2} justifyContent="space-between" onDoubleClick={onDoubleClick}>
            {spectrogram ? (
                <Grid item sx={{ width: size === 'large' ? '200px' : '120px', flexShrink: 0, display: { xs: 'none', sm: 'block' } }}>
                    <WavelengthChart data={spectrogram} small={true} />
                </Grid>
            ) : null}
            <Grid item xs={12} sm>
                <Typography gutterBottom variant={size === 'large' ? 'h6' : 'subtitle2'}>
                    {option.name}
                </Typography>
                <Typography variant="body1">{option.description}</Typography>
                {option.nameLineExtra && <Box sx={{ width: '100%', mt: 0.5 }}>{option.nameLineExtra}</Box>}
                {option.additionalItemListInfo ? (
                    <Typography variant="body2" component="div">
                        {option.additionalItemListInfo}
                    </Typography>
                ) : null}
                {error || option.lockedReason || option.linkedReason ? (
                    <Box sx={{ marginTop: '10px' }}>
                        {error ? <Alert severity="error">{error}</Alert> : null}
                        {option.lockedReason ? <Alert severity="warning">{option.lockedReason}</Alert> : null}
                        {!option.lockedReason && option.linkedReason ? <Alert severity="info">{option.linkedReason}</Alert> : null}
                    </Box>
                ) : null}
            </Grid>
        </Grid>
    );
};

export default React.memo(AutocompleteOptionWithChart);
